import React from 'react';
import { Play, Pause, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '@/lib/store';
import { themeColor } from '@/data/samples';
import { usePlayback } from '@/lib/playback';

export default function HighlightStack({ highlights, source }) {
  const { state, setState } = useStore();
  const playback = usePlayback();

  const onDownload = async (h) => {
    const ok = await playback.download(h, { source });
    if (ok) {
      toast.success(`Downloading clip · ${h.title}.mp3`, {
        description: 'Tagged with SIMNOPSIS title and logo',
      });
    } else {
      toast.info('Clip download unavailable for this highlight');
    }
  };

  return (
    <div className="glass-panel-soft divide-y divide-white/5">
      {highlights.map((h) => {
        const c = themeColor(h.theme);
        const active = state.activeHighlightId === h.id;
        const isThis = playback.highlightId === h.id;
        const isPlaying = isThis && playback.isPlaying;
        const timeLabel = isThis
          ? `${playback.clock} / ${playback.durationClock || h.length}`
          : h.start;

        return (
          <div
            key={h.id}
            className={`grid grid-cols-[3px_auto_1fr_auto_auto_auto] items-center gap-3 px-4 sm:px-5 py-4 transition-colors ${active || isThis ? 'bg-white/[0.03]' : ''}`}
            style={{ background: active || isThis ? `${c}0d` : undefined }}
            onMouseEnter={() => setState({ activeHighlightId: h.id })}
            onMouseLeave={() => setState({ activeHighlightId: null })}
            data-testid={`stack-row-${h.id}`}
          >
            <div className="h-8 w-[3px] rounded" style={{ background: c }} />
            <span
              className="tag-pill"
              style={{ background: `${c}22`, color: c, border: `1px solid ${c}44` }}
            >
              {h.themeName}
            </span>
            <div className="min-w-0">
              <div className="text-white font-medium truncate">{h.title}</div>
              <div className="text-white/50 text-xs truncate">{h.excerpt}</div>
              {isThis && (
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-[width] duration-75"
                    style={{
                      width: `${Math.min(100, playback.progress * 100)}%`,
                      background: c,
                    }}
                  />
                </div>
              )}
            </div>
            <div className="font-mono text-xs text-white/45 tabular-nums">{timeLabel}</div>
            <div className="font-mono text-xs text-white/60 tabular-nums">★ {Number(h.rating || 0).toFixed(1)}</div>
            <div className="flex items-center gap-2">
              <button
                className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
                onClick={() => {
                  setState({ activeHighlightId: h.id });
                  playback.toggle(h);
                }}
                aria-label={isPlaying ? `Pause ${h.title}` : `Play ${h.title}`}
                data-testid={`stack-play-${h.id}`}
              >
                {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
              </button>
              <button
                className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
                onClick={() => onDownload(h)}
                aria-label={`Download ${h.title}`}
                data-testid={`stack-download-${h.id}`}
              >
                <Download className="h-3 w-3" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
